import { useLocalStorage } from "@caprikorn/dynamo-utils";
import { createContext, useContext, useMemo } from "react";

import { LS_KEYS } from "@core/core.constants";

import type {
  ThemeContextProps,
  ThemeProviderProps,
  ThemeVariant,
} from "./Theme.types";

const ThemeContext = createContext<ThemeContextProps>({
  switchThemeVariant: () => true,
  themeVariant: "light",
});

const getDefaultThemeVariant = (): ThemeVariant => {
  if (window.matchMedia) {
    if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
      return "dark";
    }
    return "light";
  }

  return "auto";
};

export const ThemeProvider = ({
  children,
  initialVariant = getDefaultThemeVariant(),
}: ThemeProviderProps) => {
  const [themeVariant, setThemeVariant] = useLocalStorage<ThemeVariant>(
    LS_KEYS.THEME_VARIANT,
    initialVariant,
  );

  document.documentElement.classList.remove("theme-dark", "theme-light");

  if (themeVariant === "dark") {
    document.documentElement.classList.remove("theme-light");
    document.documentElement.classList.add("theme-dark");
  }

  if (themeVariant === "light") {
    document.documentElement.classList.remove("theme-dark");
    document.documentElement.classList.add("theme-light");
  }

  if (themeVariant === "auto") {
    document.documentElement.classList.remove("theme-dark", "theme-light");
  }

  const value = useMemo(
    () => ({
      switchThemeVariant: () => {
        setThemeVariant(themeVariant === "light" ? "dark" : "light");
      },
      themeVariant,
    }),
    [themeVariant, setThemeVariant],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context)
    throw new Error("You are not in the context of the ThemeContext");
  return context;
};
